import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { Location } from 'history';
import {
  callAlertWeb,
  callNativeWeb,
  footerShowEventWeb,
  hideProgressWeb,
  openReactWeb,
  showProgressWeb
} from 'common/appInterface';
import client from 'lib/CommonApi/client';
import { AxiosError } from 'axios';
import CardApplyStopModal from 'components/CardApplyStopModal';
import { SelectableCard } from 'components/SelectableCard';
import { getVersionUrl } from 'common/cacheOverwrite';
import { CardProductCode, NEW_ADDRESS_SCREEN_MIN_VERSION } from '../../constants/Card';
import { SuccessResponseCodeList } from '../../constants/Common';

enum CardReceiveMethod {
  NONE = '00',
  IN_PERSON = '01',
  MAILBOX = '02'
}

/** 신규 네이티브에서 열 React 주소화면 경로 (openReact_Web url 파라미터) */
const ADDRESS_REACT_URL = "card/address";
/** 주소 결과 수신 이벤트명 (APP > WEB, completedAddress_App / detail = JSON 문자열) */
const ADDRESS_RESULT_EVENT = "completedAddress_App";

/**
 * 도로명주소 뒤 참고항목 괄호 제거.
 * 신규 네이티브 korAddr 예: "경기도 용인시 기흥구 강남동로 43 (구갈동, 강남마을 자연앤아파트)"
 * → "경기도 용인시 기흥구 강남동로 43" (괄호 포함 시 verify/address 검증 실패하므로 괄호 앞까지만 사용)
 */
const stripAddressParen = (addr?: string) => (addr ?? "").split("(")[0].trim();

const CardReceiveMethodSelect = () => {

  const screenId = "APPL23";

  const location = useLocation() as Location<LocationState>;
  const navigate = useNavigate();

  const {t} = useTranslation();

  const [cardApply, setCardApply] = useState<Partial<CardApplyStep6>>(location.state.apply);
  const [isStop, setStop] = useState(false);
  const [displayCardIssueFee, setDisplayCardIssueFee] = useState<number | null>(null);
  const [selectedReceiveMethod, setSelectedReceiveMethod] = useState<CardReceiveMethod>(CardReceiveMethod.MAILBOX);

  const closeStop = useCallback(() => setStop(false), []);

  /** 표기 카드 발급비 조회 */
  const inquiryDisplayCardIssueFeeApi = useCallback(async () => {
    await client.get(`/api/app/apply/${cardApply.cardProductCode}/issueFee`, { params: location.state.query }).then((response) => {
      if (response.data.response) {
        setDisplayCardIssueFee(response.data.response.cardIssueFee);
      }
    }).catch((e: AxiosError) => {
      callAlertWeb("ERROR", "99", e.message);
    });
  }, [cardApply.cardProductCode, location]);

  /** 신규 네이티브 주소화면 열기 — openReactWeb("card/address"). 별도 라우트 없이 이 화면에서 오픈 → 네이티브 뒤로가기 시 이 화면으로 복귀 */
  const openAddressScreen = useCallback(() => {
    openReactWeb(ADDRESS_REACT_URL);
  }, []);

  /** 사진 업로드 후 다음 단계 진행 (history 등록 → 상품별 이동). 신규버전은 확인 모달 없이 바로 호출됨 */
  const clickModalYes = useCallback(async () => {
    showProgressWeb();
    await client.patch("/api/app/apply/history", {
      ...location.state.query,
      receiveSeq: cardApply.receiveSeq,
      receiveDate: cardApply.receiveDate,
      receiveStatusCode: "07",
      sendTypeDivisionCode: cardApply.receiveMethod === "01" ? "1" : "3", // 직접수령(01) → 본인수령 "1", 우편함(02) → 지정영업점배송 "3"
      deliverySelectCode: "1",
      thirdPartyReceiptYn: "Y", // 사진: receiveRelationCheck 미수신 → "Y" 고정
      addressImageCode: "5",
      cardProductCode: cardApply.cardProductCode,
      addressChangeYn: cardApply.isChangeDelivery ? "Y" : "N",
      receiveMethod: cardApply.receiveMethod
    }).then(async (response) => {
      if (response.data.code === "00") {
        // 수령지변경은 메인으로 이동
        if (cardApply.isChangeDelivery) {
          navigate("/main", {
            state: {
              query: location.state.query,
              swiperIndex: location.state.swiperIndex,
              tabIndex: location.state.tabIndex
            }
          });
        } else {
          switch (Number(cardApply.cardProductCode)) {
            case CardProductCode.DreamCard:
            case CardProductCode.PremierCard:
              navigate("/apply/step7", {
                state: {
                  query: location.state.query
                  , apply: {
                    isChangeDelivery: false
                    , callPage: 'cardApply'
                    , cardApply: cardApply
                  }
                }
              });
              break;
            case CardProductCode.WelcomeCard:
              if (cardApply.requestIssueDivisionCode?.startsWith("2") || cardApply.requestIssueDivisionCode?.startsWith("3")) {
                navigate("/apply/complet", {state: {...location.state, apply: cardApply}});
              } else {
                await client.post("/api/app/apply/chargeCardIssueFee", {
                  ...location.state.query,
                  receiveSeq: cardApply.receiveSeq,
                  receiveDate: cardApply.receiveDate
                }).then((res) => {
                  if (SuccessResponseCodeList.includes(res.data.code)) {
                    if (res.data.response.bCharged === true) {
                      navigate("/apply/complet", {state: {...location.state, apply: cardApply}});
                    } else {
                      navigate("/apply/depositCardIssuanceFee", {
                        state: {
                          ...location.state,
                          apply: cardApply,
                          storage: {
                            ...location.state.storage,
                            chargeCardIssueFeeRes: res.data.response,
                            displayCardIssueFee: displayCardIssueFee
                          }
                        }
                      });
                    }
                  } else {
                    callAlertWeb("ERROR", res.data.code, res.data.message);
                  }
                }).catch((e: AxiosError) => {
                  callAlertWeb("ERROR", "99", e.message);
                });
              }
              break;
            case CardProductCode.DirectCard:
            default:
              navigate("/apply/complet", {state: {...location.state, apply: cardApply}});
              break;
          }
        }
      }
    }).catch((e: AxiosError) => {
      callAlertWeb("ERROR", "99", e.message);
    }).finally(() => {
      hideProgressWeb();
    });
  }, [location, navigate, cardApply, displayCardIssueFee]);

  /**
   * 주소 결과 수신 (APP > WEB, completedAddress_App / detail = JSON 문자열)
   * JSON.parse 후 addressInputMethod 로 분기.
   *  "03" 사진 → address/upload → (모달 없이 바로) history 등록 → 다음 스텝 이동
   *  "01"/"02" 주소 → Juso 매핑 → (신규는 네이티브 주소화면에서 이미 confirm 완료) 컨펌화면 이동 없이
   *               바로 verify/address 검증 → history 등록 → 상품별 다음 스텝 이동.
   *               verify 실패 시 alert 후 이 화면(CardReceiveMethodSelect)에 머무름.
   */
  const callbackAddressResult = useCallback(async (event) => {
    let value: any;
    try {
      value = typeof event.detail === "string" ? JSON.parse(event.detail) : event.detail;
    } catch (e) {
      callAlertWeb("ERROR", "99", "주소 데이터 파싱 실패");
      return;
    }
    if (!value || typeof value !== "object") return;

    // ── 사진 업로드 (addressInputMethod "03") : detailAddr 없음. thirdPartyReceiptYn 은 clickModalYes 에서 "Y" 고정 전송 ──
    if (value.addressInputMethod === "03") {
      showProgressWeb();
      client.post("/api/app/apply/address/upload", {
        ...location.state.query,
        receiveSeq: cardApply.receiveSeq,
        receiveDate: cardApply.receiveDate,
        addressImageValue: decodeURIComponent(value.imageValue)
      }).then(async (response) => {
        if (response.data.code === "00") {
          // 신규버전: 확인 모달 없이 바로 다음 단계 진행 (history 등록 + 상품별 이동)
          await clickModalYes();
        } else {
          callAlertWeb("ERROR", response.data.code, response.data.message);
          hideProgressWeb();
        }
      }).catch((err: AxiosError) => {
        callAlertWeb("ERROR", "99", err.message);
        hideProgressWeb();
      });
      return;
    }

    // ── 주소 선택 (addressInputMethod "01" GPS / "02" 주소검색) : Juso 필드 매핑 ──
    const nextApply = {
      ...cardApply,
      deliverySelectCode: "3",
      thirdPartyReceiptYn: "Y", // receiveRelationCheck 미수신 → "Y" 고정
      homeZipCode: value.zipNo,
      homeEngAddress: value.engAddr,
      homeNewAddress: stripAddressParen(value.korAddr), // 괄호(참고항목) 제거 — verify/address 검증 실패 방지
      homeNewDetailAddress: value.detailAddr ?? "",
      homeRoadManagementNo: value.rnMgtSn,
      admCd: value.admCd,
      udrtYn: value.udrtYn,
      buldMnnm: value.buldMnnm,
      buldSlno: value.buldSlno,
      siNm: value.siNm,
      sggNm: value.sggNm,
      rn: value.rn,
      emdNm: value.emdNm
    };

    showProgressWeb();
    // 1) 주소검증 — 실패 시 alert 후 이 화면에 머무름 (컨펌화면 없음)
    try {
      const verifyRes = await client.get("/api/app/apply/verify/address", {
        params: {
          ...location.state.query,
          homeZipCode: nextApply.homeZipCode,
          homeNewAddress: nextApply.homeNewAddress,
          homeNewDetailAddress: nextApply.homeNewDetailAddress
        }
      });
      if (verifyRes.data.code !== "00") {
        alert(verifyRes.data.message);
        hideProgressWeb();
        return;
      }
    } catch (e: any) {
      callAlertWeb("ERROR", "99", e?.message ?? "");
      hideProgressWeb();
      return;
    }

    // 2) 카드신청내역 등록 → 상품별 다음 스텝 이동
    await client.patch("/api/app/apply/history", {
      ...location.state.query,
      receiveSeq: nextApply.receiveSeq,
      receiveDate: nextApply.receiveDate,
      receiveStatusCode: "07",
      sendTypeDivisionCode: nextApply.receiveMethod === "01" ? "1" : "3",
      deliverySelectCode: nextApply.deliverySelectCode,
      homeZipCode: nextApply.homeZipCode,
      homeNewAddress: nextApply.homeNewAddress,
      homeNewDetailAddress: nextApply.homeNewDetailAddress,
      homeEngAddress: nextApply.homeEngAddress,
      homeRoadManagementNo: nextApply.homeRoadManagementNo,
      thirdPartyReceiptYn: nextApply.thirdPartyReceiptYn,
      cardProductCode: nextApply.cardProductCode,
      addressChangeYn: nextApply.isChangeDelivery ? "Y" : "N",
      receiveMethod: nextApply.receiveMethod,
      buldMnnm: nextApply.buldMnnm,
      buldSlno: nextApply.buldSlno,
      siNm: nextApply.siNm,
      sggNm: nextApply.sggNm,
      rn: nextApply.rn,
      emdNm: nextApply.emdNm
    }).then(async (response) => {
      if (response.data.code === "00") {
        if (nextApply.isChangeDelivery) {
          navigate("/main", {
            state: {
              query: location.state.query,
              swiperIndex: location.state.swiperIndex,
              tabIndex: location.state.tabIndex
            }
          });
        } else {
          switch (Number(nextApply.cardProductCode)) {
            case CardProductCode.DreamCard:
            case CardProductCode.PremierCard:
              navigate("/apply/step7", {
                state: {
                  query: location.state.query,
                  apply: {
                    isChangeDelivery: false,
                    callPage: 'cardApply',
                    cardApply: nextApply,
                    ...nextApply
                  }
                }
              });
              break;
            case CardProductCode.WelcomeCard:
              if (nextApply.requestIssueDivisionCode?.startsWith("2") || nextApply.requestIssueDivisionCode?.startsWith("3")) {
                navigate("/apply/complet", {state: {...location.state, apply: nextApply}});
              } else {
                await client.post("/api/app/apply/chargeCardIssueFee", {
                  ...location.state.query,
                  receiveSeq: nextApply.receiveSeq,
                  receiveDate: nextApply.receiveDate
                }).then((res) => {
                  if (SuccessResponseCodeList.includes(res.data.code)) {
                    if (res.data.response.bCharged === true) {
                      navigate("/apply/complet", {state: {...location.state, apply: nextApply}});
                    } else {
                      navigate("/apply/depositCardIssuanceFee", {
                        state: {
                          ...location.state,
                          apply: nextApply,
                          storage: {
                            ...location.state.storage,
                            chargeCardIssueFeeRes: res.data.response,
                            displayCardIssueFee: displayCardIssueFee
                          }
                        }
                      });
                    }
                  } else {
                    callAlertWeb("ERROR", res.data.code, res.data.message);
                  }
                }).catch((e: AxiosError) => {
                  callAlertWeb("ERROR", "99", e.message);
                });
              }
              break;
            case CardProductCode.DirectCard:
            default:
              navigate("/apply/complet", {state: {...location.state, apply: nextApply}});
              break;
          }
        }
      } else {
        callAlertWeb("ERROR", response.data.code, response.data.message);
      }
    }).catch((e: AxiosError) => {
      callAlertWeb("ERROR", "99", e.message);
    }).finally(() => {
      hideProgressWeb();
    });
  }, [cardApply, location, navigate, displayCardIssueFee, clickModalYes]);

  const clickNext = useCallback(() => {
    const appVersion = parseInt(location.state.query.apiVersion);
    if (appVersion >= NEW_ADDRESS_SCREEN_MIN_VERSION) {
      // 신규: 이 화면에서 직접 네이티브 주소화면 오픈 (별도 라우트 없음 → 네이티브 뒤로가기 시 이 화면으로 복귀)
      setCardApply((prev) => ({ ...prev, receiveMethod: selectedReceiveMethod }));
      openAddressScreen();
    } else {
      // 구버전: 원본 CardDeliverySelect
      navigate("/apply/step6", { state: { ...location.state, apply: { ...location.state.apply, receiveMethod: selectedReceiveMethod } } });
    }
  }, [navigate, location.state, selectedReceiveMethod, openAddressScreen]);

  const callbackBackKeyEvent = useCallback(() => {
    // 수령지변경 처리
    if (cardApply.isChangeDelivery) {
      navigate(-1);
    }
    // 카드발급신청 처리
    else {
      setStop(true);
    }
  }, [navigate, cardApply.isChangeDelivery]);

  // TODO dev 테스트용 (주소검색 결과 시뮬레이트)
  const testAddressCall = useCallback(() => {
    const payload = JSON.stringify({
      addressInputMethod: "02",
      zipNo: "06236", roadAddr: "12 Teheran-ro, Gangnam-gu, Seoul", korAddr: "서울특별시 강남구 테헤란로 12",
      rnMgtSn: "116804166011", admCd: "1168010100", udrtYn: "0", buldMnnm: "12", buldSlno: "0",
      siNm: "서울특별시", sggNm: "강남구", rn: "테헤란로", emdNm: "역삼동", detailAddr: "3동 401호"
    });
    document.dispatchEvent(new CustomEvent(ADDRESS_RESULT_EVENT, { detail: payload }));
  }, []);

  // 주소 결과 수신 리스너 (네이티브 completedAddress_App → appCallbackInterface.js → CustomEvent)
  useEffect(() => {
    document.addEventListener(ADDRESS_RESULT_EVENT, callbackAddressResult);
    return () => document.removeEventListener(ADDRESS_RESULT_EVENT, callbackAddressResult);
  }, [callbackAddressResult]);

  useEffect(() => {
    // callNative_Web 처리 (WEB > APP)
    callNativeWeb(screenId, t(screenId + ".title", "Select Card Receiving Method"));
    // 하단바 숨기기
    footerShowEventWeb("N");
    // 표기 발급비 조회 (사진결과 후 발급비 화면 대비)
    inquiryDisplayCardIssueFeeApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 뒤로가기 이벤트 리스너 (backKeyEvent_App (APP > WEB))
  useEffect(() => {
    document.addEventListener("backpress", callbackBackKeyEvent);
    return () => document.removeEventListener("backpress", callbackBackKeyEvent);
  }, [callbackBackKeyEvent]);

  return (
    <div id="wrap">
      <div id="container">
        <div className={"cardReceiveMethod"}>
          <p className={"title"}>{t(screenId + '.explan01', 'How to receive your card?')}</p>
          <div className={'content'}>
            <SelectableCard
              title={t(screenId + '.selectTitle01', 'Receive in my mailbox or front door (택배)')}
              description={t(screenId + '.selectExplan01', 'Receive your card without meeting anyone.')}
              selected={selectedReceiveMethod === CardReceiveMethod.MAILBOX}
              showImage
              imageSrc={getVersionUrl('/assets/images/content/img_receive_mailbox.png')}
              onClick={() => setSelectedReceiveMethod(CardReceiveMethod.MAILBOX)}
            />
            <SelectableCard
              title={t(screenId + '.selectTitle02', 'Receive in person')}
              description={t(screenId + '.selectExplan02', 'Before delivery, you will receive a call. The card must be received by you in person.')}
              selected={selectedReceiveMethod === CardReceiveMethod.IN_PERSON}
              showImage
              imageSrc={getVersionUrl('/assets/images/content/img_receive_inperson.png')}
              onClick={() => setSelectedReceiveMethod(CardReceiveMethod.IN_PERSON)}
            />
          </div>
          {process.env.REACT_APP_PROFILE !== "prod" &&
            <button className="btnPrimaryTy01" style={{width: 'auto', margin: '12px 16px'}} type="button" onClick={testAddressCall}><span>주소검색 결과(테스트)</span></button>
          }
        </div>
        <div className="btnArea">
          <div className="btnWrapTy01">
            <button type="button" className="btnPrimaryTy01"
                    disabled={CardReceiveMethod.NONE === selectedReceiveMethod}
                    onClick={clickNext}
            >
              <span>{t("COMMON.button.next")}</span>
            </button>
          </div>
        </div>
        <CardApplyStopModal isStop={isStop} closeStop={closeStop} />
      </div>
    </div>

  );
}

export default CardReceiveMethodSelect;
